import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import type { LineElement } from "@ai-teach/types";
import { useEffect, useRef, useState } from "react";
import { BiChevronDown } from "react-icons/bi";
import { MdLineWeight } from "react-icons/md";
import { TbLine, TbLineDashed, TbLineDotted } from "react-icons/tb";

interface LineElementToolbarProps {
  lineElement: LineElement;
}

export function LineElementToolbar({ lineElement }: LineElementToolbarProps) {
  const [isStylePickerOpen, setIsStylePickerOpen] = useState<boolean>(false);
  const [isStrokePickerOpen, setIsStrokePickerOpen] = useState<boolean>(false);
  const [localStroke, setLocalStroke] = useState(lineElement.stroke || "#000000");
  const strokeColorTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const stylePickerRef = useRef<HTMLDivElement | null>(null);
  const strokePickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLocalStroke(lineElement.stroke || "#000000");
  }, [lineElement.stroke]);

  const dispatch = useAppDispatch();
  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);

  if (!slide) return null;

  const selectedElement = slide.elements.find((el) => el.id === selectedId);

  // Close style picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (stylePickerRef.current && !stylePickerRef.current.contains(e.target as Node)) {
        setIsStylePickerOpen(false);
      }
      if (strokePickerRef.current && !strokePickerRef.current.contains(e.target as Node)) {
        setIsStrokePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLineStyleChange = (style: "solid" | "dashed" | "dotted") => {
    if (!selectedId) return;

    dispatch(
      updateElementProps({
        id: selectedId,
        props: {
          lineStyle: style,
        },
      })
    );
    setIsStylePickerOpen(false);
  };

  const handleStrokeWeightChange = (width: number) => {
    if (!selectedId) return;

    dispatch(
      updateElementProps({
        id: selectedId,
        props: {
          strokeWidth: width,
        },
      })
    );
  };

  const handleColorChange = (color: string) => {
    setLocalStroke(color);
    if (strokeColorTimer.current) clearTimeout(strokeColorTimer.current);
    strokeColorTimer.current = setTimeout(() => {
      if (!selectedId) return;
      dispatch(
        updateElementProps({
          id: selectedId,
          props: { stroke: color },
        })
      );
    }, 80);
  };

  const selectedLineStyle = lineElement.lineStyle;

  const getLineStyleIcon = () => {
    switch (selectedLineStyle) {
      case "dashed":
        return <TbLineDashed className="w-5 h-5" />;
      case "dotted":
        return <TbLineDotted className="w-5 h-5" />;
      case "solid":
        return <TbLine className="w-5 h-5" />;
      default:
        return <TbLine className="w-5 h-5" />;
    }
  };

  const strokeWeightOptions = [2, 4, 6, 8, 12, 16];

  if (!selectedElement) return null;

  return (
    <>
      {/* Stroke Color Picker */}
      <div className="flex items-center gap-1 relative group">
        <label htmlFor="line-color" className="cursor-pointer">
          <div className="relative">
            <div
              className="w-8 h-8 rounded-lg border-2 border-gray-300 transition-all duration-200 group-hover:border-primary-400 group-hover:scale-105 group-hover:shadow-md"
              style={{ backgroundColor: localStroke }}
            />
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
              <div className="w-3 h-3 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 to-blue-400" />
            </div>
          </div>
        </label>
        <input
          id="line-color"
          type="color"
          value={localStroke}
          onChange={(e) => handleColorChange(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          title="Line Color"
        />
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Line Style Picker */}
      <div className="relative" ref={stylePickerRef}>
        <button
          type="button"
          onClick={() => setIsStylePickerOpen((v) => !v)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white/50 hover:border-primary-400 hover:bg-gray-50 transition-all duration-200"
        >
          {getLineStyleIcon()}
          <BiChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isStylePickerOpen ? "rotate-180" : ""}`}
          />
        </button>

        {/* Line Style Dropdown */}
        {isStylePickerOpen && (
          <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 z-50 min-w-[150px]">
            {[
              { value: "solid", icon: TbLine, label: "Solid" },
              { value: "dashed", icon: TbLineDashed, label: "Dashed" },
              { value: "dotted", icon: TbLineDotted, label: "Dotted" },
            ].map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleLineStyleChange(value as "solid" | "dashed" | "dotted")}
                className={`w-full flex items-center gap-3 px-3 py-2 transition-all duration-200 ${
                  selectedLineStyle === value
                    ? "bg-primary-50 text-primary-600"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{label}</span>
                {selectedLineStyle === value && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Stroke Weight Picker */}
      <div className="relative" ref={strokePickerRef}>
        <button
          type="button"
          onClick={() => setIsStrokePickerOpen((v) => !v)}
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white/50 hover:border-primary-400 hover:bg-gray-50 transition-all duration-200"
        >
          <MdLineWeight className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700 min-w-[2ch]">
            {lineElement.strokeWidth}
          </span>
          <BiChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isStrokePickerOpen ? "rotate-180" : ""}`}
          />
        </button>

        {/* Stroke Weight Dropdown */}
        {isStrokePickerOpen && (
          <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl py-2 z-50 min-w-[140px]">
            <div className="px-3 pb-2 mb-1 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Stroke Weight
              </span>
            </div>
            <div className="px-1.5 space-y-0.5">
              {strokeWeightOptions.map((weight) => (
                <button
                  key={weight}
                  type="button"
                  onClick={() => {
                    handleStrokeWeightChange(weight);
                    setIsStrokePickerOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition-all duration-200 ${
                    lineElement.strokeWidth === weight
                      ? "bg-primary-50 text-primary-600"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <div
                    className="rounded-full transition-all"
                    style={{
                      width: `${Math.min(weight * 1.5, 18)}px`,
                      height: `${Math.min(weight * 1.5, 18)}px`,
                      backgroundColor:
                        lineElement.strokeWidth === weight ? "var(--color-primary-500)" : "#9ca3af",
                    }}
                  />
                  <span className="text-sm font-medium">{weight}px</span>
                  {lineElement.strokeWidth === weight && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
